import React, { useState, useEffect } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import {
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  Search,
  X,
  Eye,
  Users,
  CheckSquare,
  Clock,
  CircleCheck,
  Download,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { auth, db, messaging } from "../../firebase";
import { collection, addDoc, query, where, getDocs, serverTimestamp } from "firebase/firestore";
import { getToken } from "firebase/messaging";
import InfoModal from "../../components/InfoModal";
import { useAuth } from "../../context/AuthContext";
import featureGatingService from "../../services/featureGatingService";
import CaseLimitDialog from "../../components/CaseLimitDialog";
import { scGet } from "../../services/supremeCourtApi";
import { uploadSciOrderPdfs } from "./sciFileSync";
import { STATUS_PILL_BASE, statusPillSoft, statusPillSolid } from "../../styles/statusPill";

function SupremeCourtResults() {
  const location = useLocation();
  const { data, searchType, searchPayload } = location.state || {};
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseDetails, setCaseDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Export states
  const [exporting, setExporting] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateCaseInfo, setDuplicateCaseInfo] = useState(null);
  const [showCaseLimitDialog, setShowCaseLimitDialog] = useState(false);
  const [caseLimitData, setCaseLimitData] = useState({ currentCount: 0, maxLimit: 0, planName: '' });

  // Bulk export states
  const [selectedCases, setSelectedCases] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    total: 0, exported: 0, currentCase: null, startTime: null, failedCases: []
  });
  const [duplicateCases, setDuplicateCases] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  // Results list view state
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const casesPerPage = 10;

  const { globalUser } = useAuth();
  const userId = globalUser?.uid;
  const [userMobile, setUserMobile] = useState("");

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;
      try {
        const userQuery = query(collection(db, "lawyers"), where("uid", "==", userId));
        const userDocSnapshot = await getDocs(userQuery);
        if (!userDocSnapshot.empty) {
          const userData = userDocSnapshot.docs[0].data();
          setUserMobile(userData?.mobileWithCountryCode || "");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };
    fetchUserData();
  }, [userId]);

  const showInfo = () => setIsInfoModalOpen(true);
  const closeInfoModal = () => setIsInfoModalOpen(false);

  if (!data) {
    return (
      <>
                                <div className="relative min-h-screen flex flex-col items-center justify-center">
          <div className="mb-4"><FindCaseStepper current={3} /></div>
          <p className="text-xl text-slate-500">No data available. Please search again.</p>
          <Link to="/portal/fetchfromsupremecourt" className="text-yellow-500 underline mt-4">
            Back to Search
          </Link>
        </div>
      </>
    );
  }

  const fetchDetails = async (diaryNo, diaryYear) => {
    setLoading(true);
    setError("");
    try {
      const apiPath = searchType || "diary-number";
      const res = await scGet(`/api/${apiPath}/details`, { diaryNo, diaryYear, language: "en" });
      const detailData = res.body;
      if (detailData?.error) {
        setError(detailData.error);
        return;
      }
      const details = detailData?.parsed?.details || detailData;
      setCaseDetails(details);
      setSelectedCase({ diaryNo, diaryYear });
    } catch (err) {
      setError("Failed to fetch case details.");
    } finally {
      setLoading(false);
    }
  };

  const getRecords = () => {
    if (data?.parsed?.records && Array.isArray(data.parsed.records)) {
      return data.parsed.records;
    }
    if (Array.isArray(data)) return data;
    if (data?.results) return data.results;
    if (data?.cases) return data.cases;
    return [];
  };

  // Rows carry their absolute index into getRecords() so selection — and
  // exportSelectedCases, which reads results[index] — stays correct while the
  // list is filtered or paginated.
  const getFilteredRows = () => {
    const rows = getRecords().map((item, index) => ({ item, index }));
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(({ item }) =>
      [item.diaryText, item.diaryNo, item.caseNo, item.petitioner, item.respondent, item.status].some(
        (f) => f != null && String(f).toLowerCase().includes(q)
      )
    );
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const parseDiaryNo = (diaryNoStr) => {
    if (!diaryNoStr) return { no: "", year: "" };
    const parts = String(diaryNoStr).split("/");
    if (parts.length === 2) {
      return { no: parts[0].trim(), year: parts[1].trim() };
    }
    return { no: diaryNoStr, year: "" };
  };

  // Resolve { no, year } from a search record. The backend returns diaryNo as the
  // number ONLY ("5337") with the year in a separate diaryYear field, plus a
  // combined diaryText ("5337 / 2024"). Prefer the explicit fields; fall back to
  // splitting a combined value for older/other record shapes.
  const getDiaryParts = (item) => {
    const explicitYear = item?.diaryYear || item?.diary_year;
    const no = parseDiaryNo(item?.diaryNo).no || item?.diaryNo || "";
    if (explicitYear) {
      return { no: String(no).trim(), year: String(explicitYear).trim() };
    }
    return parseDiaryNo(item?.diaryText || item?.diaryNo);
  };


  const InfoRow = ({ label, value }) => (
    <div className="flex py-2">
      <span className="w-48 text-sm text-slate-600 shrink-0">{label}:</span>
      <span className="font-bold flex-1">{value || "N/A"}</span>
    </div>
  );

  // PR #4 changed GET /details: it now returns a normalized DTO
  //   { searchResult, caseDetails:{...camelCase}, listingDates, notices,
  //     interlocutoryApplications, earlierCourtDetails, judgementOrders, ... }
  // Older builds returned { parsed: { details: {...snake_case} } }. These
  // accessors read whichever shape is present so the page works either way.
  const getCore = (cd) =>
    cd?.caseDetails && typeof cd.caseDetails === "object" ? cd.caseDetails : cd || {};

  const asList = (val) => (Array.isArray(val) ? val : val ? [val].filter(Boolean) : []);

  // Combined case number string (new shape: string; old shape: array).
  const getCaseNumberStr = (core) =>
    Array.isArray(core.case_number)
      ? core.case_number.join(" ")
      : core.caseNumber || core.case_number || "";

  const getDiaryNumber = (core) => core.diaryNumber || core.diary_number || "";

  // --- Export helpers ---

  const getStatusFromDetails = (details) => {
    const core = getCore(details);
    const status =
      core.disposalStatus || details?.searchResult?.status || details?.status || core.status || "";
    const s = String(status).toLowerCase();
    if (s.includes("disposed") || s.includes("decided")) return "disposed";
    return "pending";
  };

  const cleanPartyName = (name) => {
    if (!name) return "N/A";
    return name.replace(/^\d+\s*/, "").trim() || "N/A";
  };

  // SCI order titles often repeat the document name around an embedded date,
  // e.g. "ROP - of Main Case 13-01-2026 ROP - of Main Case". Collapse it.
  const cleanOrderName = (raw) => {
    let s = (raw || "").toString().replace(/\s+/g, " ").trim();
    if (!s) return "Order";
    s = s.replace(/^(.+?)\s+\d{1,2}-\d{1,2}-\d{2,4}\s+\1$/i, "$1");
    s = s.replace(/^(.+?)\s+\1$/i, "$1");
    return s.trim();
  };

  // Extract only the date part from SC API fields
  // e.g. "24-03-2026 [HON'BLE MR. JUSTICE...]" → "24/03/2026"
  // e.g. "02-01-2024 03:25 PM" → "02/01/2024"
  const extractDate = (raw) => {
    if (!raw) return "";
    const match = raw.match(/(\d{2})[/-](\d{2})[/-](\d{4})/);
    if (match) {
      return `${match[1]}/${match[2]}/${match[3]}`;
    }
    return raw.trim();
  };

  const checkCaseLimitBeforeExport = async (count = 1, targetCollection = "pending") => {
    if (!globalUser?.uid) return false;
    const result = await featureGatingService.checkCaseLimitBeforeExport(
      globalUser.uid, count, targetCollection
    );
    if (!result.canProceed) {
      setCaseLimitData({
        currentCount: result.currentCount,
        maxLimit: result.maxLimit,
        planName: result.planName
      });
      setShowCaseLimitDialog(true);
      return false;
    }
    return true;
  };

  const checkDuplicate = async (caseNo, userId, collectionsToCheck) => {
    for (const col of collectionsToCheck) {
      const q = query(
        collection(db, col),
        where("caseNo", "==", caseNo),
        where("owner", "==", userId)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        return { isDuplicate: true, collection: col, docId: snapshot.docs[0].id };
      }
    }
    return { isDuplicate: false };
  };

  const buildCaseData = (details, user, subscriptionMetadata) => {
    const core = getCore(details);
    const petitioners = asList(core.petitioners ?? core.petitioner_s);
    const respondents = asList(core.respondents ?? core.respondent_s);
    const petAdvocates = asList(core.petitionerAdvocates ?? core.petitioner_advocate_s);
    const resAdvocates = asList(core.respondentAdvocates ?? core.respondent_advocate_s);
    const caseNumberStr = getCaseNumberStr(core);
    const diaryNumber = getDiaryNumber(core);

    const firstPetitioner = petitioners.length > 0 ? cleanPartyName(petitioners[0]) : "Unknown";
    const firstRespondent = respondents.length > 0 ? cleanPartyName(respondents[0]) : "Unknown";

    const allPetitioners = petitioners
      .map(p => ({ name: cleanPartyName(p) }))
      .filter(p => p.name && p.name !== "N/A");
    const allRespondents = respondents
      .map(r => ({ name: cleanPartyName(r) }))
      .filter(r => r.name && r.name !== "N/A");
    const allPetitionerAdvocates = petAdvocates
      .map(a => ({ name: cleanPartyName(a) }))
      .filter(a => a.name && a.name !== "N/A");
    const allRespondentAdvocates = resAdvocates
      .map(a => ({ name: cleanPartyName(a) }))
      .filter(a => a.name && a.name !== "N/A");

    const caseStatus = getStatusFromDetails(details);

    // Rich tab sections from the normalized DTO — persisted so the case-detail
    // screen can show Hearing History, IAs, Notices, Earlier Courts and Orders.
    const listingDates = asList(details.listingDates);
    const interlocutoryApplications = asList(details.interlocutoryApplications);
    const notices = asList(details.notices);
    const earlierCourtDetails = asList(details.earlierCourtDetails);
    const judgementOrders = asList(details.judgementOrders);
    const otherDocuments = asList(details.otherDocuments);

    // Hearing/listing history → caseHistory (SC-native shape rendered on the
    // case-detail "Case History" tab).
    const caseHistory = listingDates.map((l) => ({
      clDate: l.clDate || "",
      stage: l.stage || "",
      purpose: l.purpose || "",
      judges: asList(l.judges),
      remarks: l.remarks || "",
      listedStatus: l.listedStatus || "",
    }));

    // Orders list for the "Judgements / Orders" tab.
    const orders = judgementOrders.map((o) => ({
      documentName: cleanOrderName(o.documentName || o.description),
      date: o.date || "",
      url: o.url || o.documentUrl || "",
    }));

    return {
      caseNo: diaryNumber || caseNumberStr || "",
      filingNumber: diaryNumber || "",
      caseType: core.category || "",
      caseFiledDate: extractDate(core.filedDate || core.filed_on),
      nextHearingDate: extractDate(core.presentLastListedOn || core.present_last_listed_on),
      previousHearingDate: "",
      courtName: "Supreme Court of India",
      petitionerName: firstPetitioner,
      partyContactNo: "",
      respondentName: firstRespondent,
      appearingFor: "",
      petitionerAdvocate: allPetitionerAdvocates[0]?.name || "",
      respondentAdvocate: allRespondentAdvocates[0]?.name || "",
      allPetitioners,
      allRespondents,
      allPetitionerAdvocates,
      allRespondentAdvocates,
      team: [user.uid],
      files: [],
      casenotes: "",
      status: caseStatus,
      owner: user.uid,
      caseHistory,
      orders,
      payload: {
        diaryNumber: diaryNumber || "",
        caseNumber: caseNumberStr || "",
        cnrNumber: core.cnrNumber || core.cnr_number || "",
        statusStage: core.statusStage || core.status_stage || "",
        section: core.section || "",
        category: core.category || "",
        registeredDate: core.registeredDate || "",
        verifiedDate: core.verifiedDate || "",
        bench: asList(core.bench),
        judges: asList(core.judges),
        disposalType: core.disposalType || "",
        searchType: searchType || "diary-number",
        searchPayload: searchPayload || {},
        exportedAt: new Date().toISOString(),
        caseexportedfrom: "SupremeCourt",
        // Full rich sections for the case-detail tabs.
        sciDetails: {
          listingDates,
          interlocutoryApplications,
          notices,
          earlierCourtDetails,
          judgementOrders,
          otherDocuments,
        },
      },
      ...(subscriptionMetadata && { subscriptionMetadata }),
      timestamp: serverTimestamp(),
    };
  };

  const createEventAndReminder = async (caseData, user, caseId, collectionName) => {
    const nextHearingDate = caseData.nextHearingDate;
    if (!nextHearingDate) return;

    try {
      // Try to parse date - SC dates may be in various formats like "01/01/2026" or "01-01-2026"
      let eventDate;
      const slashParts = nextHearingDate.split("/");
      const hyphenParts = nextHearingDate.split("-");

      if (slashParts.length === 3) {
        eventDate = new Date(`${slashParts[2]}-${slashParts[1]}-${slashParts[0]}`);
      } else if (hyphenParts.length === 3) {
        eventDate = new Date(`${hyphenParts[2]}-${hyphenParts[1]}-${hyphenParts[0]}`);
      } else {
        eventDate = new Date(nextHearingDate);
      }

      if (isNaN(eventDate.getTime())) return;

      // Only create events for future hearing dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const hearingDay = new Date(eventDate);
      hearingDay.setHours(0, 0, 0, 0);
      if (hearingDay < today) {
        console.log("SC: skipping event creation — next hearing date is in the past:", nextHearingDate);
        return;
      }

      const caseNoForEvent = caseData.caseNo || "";
      const formatYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

      // Event 1: 8:00 AM IST on the next hearing date (visible)
      const reminderDate = new Date(eventDate);
      reminderDate.setHours(8, 0, 0, 0);

      // Event 2: 6:00 PM IST on the next hearing date (hidden)
      const eventTimeIST = new Date(eventDate);
      eventTimeIST.setHours(18, 0, 0, 0);

      const now = new Date();

      if (reminderDate > now) {
        const reminderEventData = {
          "Case No": caseNoForEvent,
          date: formatYMD(reminderDate),
          id: Math.floor(Math.random() * 90000) + 10000,
          mobile: userMobile || null,
          time: "08:00 AM",
          time1: reminderDate.getTime(),
          title: `${caseData.petitionerName} VS ${caseData.respondentName}`,
          description: `Hearing Reminder : Case No. - ${caseNoForEvent}`,
          isScheduled: true,
          status: caseData.status,
          eventType: "reminder",
        };
        const reminderEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), reminderEventData);
        await addDoc(collection(db, "eventReminders"), {
          eventId: reminderEventRef.id,
          reminderTime: reminderDate,
          eventTitle: `Hearing Reminder : Case No. - ${caseNoForEvent}`,
          caseNo: caseNoForEvent,
          caseId: caseId || "",
          collection: collectionName || caseData.status,
          status: "pending",
          createdAt: new Date(),
          scheduledBy: user.uid,
          recipientId: user.uid,
        });
      }

      if (eventTimeIST > now) {
        const mainEventData = {
          "Case No": caseNoForEvent,
          date: formatYMD(eventDate),
          id: Math.floor(Math.random() * 90000) + 10000,
          mobile: userMobile || null,
          time: "06:00 PM",
          time1: eventTimeIST.getTime(),
          title: `Update Next Hearing: ${caseData.petitionerName} VS ${caseData.respondentName}`,
          description: `Case No. - ${caseNoForEvent}`,
          isScheduled: true,
          status: caseData.status,
          eventType: "updateReminder",
          isHidden: true,
        };
        const mainEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), mainEventData);
        await addDoc(collection(db, "eventReminders"), {
          eventId: mainEventRef.id,
          reminderTime: eventTimeIST,
          eventTitle: `Update Next Hearing : ${caseNoForEvent}`,
          caseNo: caseNoForEvent,
          caseId: caseId || "",
          collection: collectionName || caseData.status,
          status: "pending",
          createdAt: new Date(),
          scheduledBy: user.uid,
          recipientId: user.uid,
        });
      }
    } catch (err) {
      console.error("Event/reminder creation error:", err);
    }
  };

  // --- Single case export (from details view) ---
  const handleExportCase = async () => {
    try {
      const user = auth.currentUser;
      if (!user) { alert("User not authenticated"); return; }
      if (!caseDetails) { alert("No case details available to export"); return; }

      const caseStatus = getStatusFromDetails(caseDetails);
      const targetCollection = caseStatus === "disposed" ? "disposed" : "pending";

      const canProceed = await checkCaseLimitBeforeExport(1, targetCollection);
      if (!canProceed) return;

      setExporting(true);

      const caseNo = getDiaryNumber(getCore(caseDetails));
      if (caseNo) {
        const dupResult = await checkDuplicate(caseNo, user.uid, ["pending", "disposed", "decided"]);
        if (dupResult.isDuplicate) {
          setDuplicateCaseInfo({ caseNo, collection: dupResult.collection });
          setDuplicateModalOpen(true);
          setExporting(false);
          return;
        }
      }

      let subscriptionMetadata = null;
      if (caseStatus === "pending") {
        const pendingQuery = query(collection(db, "pending"), where("owner", "==", user.uid));
        const pendingSnapshot = await getDocs(pendingQuery);
        subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(
          user.uid, pendingSnapshot.size
        );
      }

      const caseData = buildCaseData(caseDetails, user, subscriptionMetadata);
      const caseDocRef = await addDoc(collection(db, caseStatus), caseData);

      // Save judgement/order PDFs into the case's "Orders" folder.
      await uploadSciOrderPdfs({
        user,
        caseId: caseDocRef.id,
        caseNo: caseData.caseNo,
        caseCollection: caseStatus,
        judgementOrders: asList(caseDetails.judgementOrders),
      });

      try {
        await getToken(messaging, {
          vapidKey: "BOSi0E-WalUbqOuB5su9XW2J39ot0D4x-fSPRYo_AxMVloqe7jIkpYXf5J007lXs1D_ZZB9YH1Hc0X-7jQrqR38",
        });
      } catch (err) {
        console.warn("Could not get FCM token:", err.message);
      }

      await createEventAndReminder(caseData, user, caseDocRef.id, caseStatus);
      showInfo();
    } catch (err) {
      console.error("Export error:", err);
      alert(`Error exporting case: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  // --- Bulk export (from results list) ---
  const handleCaseSelect = (index) => {
    const newSelected = new Set(selectedCases);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedCases(newSelected);
    const rows = getFilteredRows();
    setSelectAll(rows.length > 0 && rows.every((r) => newSelected.has(r.index)));
  };

  // Select/deselect every row currently visible under the search filter.
  const handleSelectAll = () => {
    const rows = getFilteredRows();
    const newSelected = new Set(selectedCases);
    if (selectAll) {
      rows.forEach((r) => newSelected.delete(r.index));
      setSelectAll(false);
    } else {
      rows.forEach((r) => newSelected.add(r.index));
      setSelectAll(rows.length > 0);
    }
    setSelectedCases(newSelected);
  };

  const exportSelectedCases = async () => {
    try {
      const user = auth.currentUser;
      if (!user) { alert("User not authenticated"); return; }

      const results = getRecords();
      const selectedIndices = Array.from(selectedCases);
      if (selectedIndices.length === 0) { alert("No cases selected to export"); return; }

      const canProceed = await checkCaseLimitBeforeExport(selectedIndices.length, "pending");
      if (!canProceed) return;

      setExporting(true);
      setExportProgress({
        total: selectedIndices.length, exported: 0,
        currentCase: "Preparing to export cases...",
        startTime: Date.now(), failedCases: []
      });
      await new Promise(resolve => setTimeout(resolve, 100));

      const duplicatesFound = [];
      let successfulExports = 0;
      let failedExports = 0;

      const pendingQuery = query(collection(db, "pending"), where("owner", "==", user.uid));
      const pendingSnapshot = await getDocs(pendingQuery);
      let currentPendingCount = pendingSnapshot.size;

      for (const index of selectedIndices) {
        try {
          const item = results[index];
          if (!item) continue;

          const { no: diaryNum, year: diaryYear } = getDiaryParts(item);

          setExportProgress(prev => ({
            ...prev, currentCase: `Fetching details: ${item.diaryNo || item.caseNo || `Case ${index + 1}`}`
          }));
          await new Promise(resolve => setTimeout(resolve, 50));

          // Fetch details for this case
          const apiPath = searchType || "diary-number";
          const res = await scGet(`/api/${apiPath}/details`, { diaryNo: diaryNum, diaryYear, language: "en" });
          const detailData = res.body;
          const details = detailData?.parsed?.details || detailData;

          if (!details || detailData?.error) {
            failedExports++;
            setExportProgress(prev => ({
              ...prev, exported: prev.exported + 1,
              failedCases: [...prev.failedCases, item.diaryNo || `Case ${index + 1}`],
              currentCase: `Failed: ${item.diaryNo || `Case ${index + 1}`}`
            }));
            continue;
          }

          const caseNo = getDiaryNumber(getCore(details));
          const caseStatus = getStatusFromDetails(details);

          // Check duplicate
          let isDuplicate = false;
          if (caseNo) {
            const dupResult = await checkDuplicate(caseNo, user.uid, ["pending", "disposed", "decided"]);
            if (dupResult.isDuplicate) {
              isDuplicate = true;
              duplicatesFound.push({ caseNo, collection: dupResult.collection, caseItem: item });
              setExportProgress(prev => ({
                ...prev, exported: prev.exported + 1,
                currentCase: `Skipped duplicate: ${item.diaryNo}`
              }));
            }
          }

          if (!isDuplicate) {
            setExportProgress(prev => ({
              ...prev, currentCase: `Exporting: ${item.diaryNo || item.caseNo || `Case ${index + 1}`}`
            }));

            // Check per-case limit
            const limitResult = await featureGatingService.checkCaseLimitBeforeExport(
              user.uid, 1, caseStatus === "disposed" ? "disposed" : "pending"
            );
            if (!limitResult.canProceed) {
              setCaseLimitData({
                currentCount: limitResult.currentCount,
                maxLimit: limitResult.maxLimit,
                planName: limitResult.planName
              });
              setShowCaseLimitDialog(true);
              break;
            }

            let subscriptionMetadata = null;
            if (caseStatus === "pending") {
              subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(
                user.uid, currentPendingCount
              );
            }

            const caseData = buildCaseData(details, user, subscriptionMetadata);
            const bulkDocRef = await addDoc(collection(db, caseStatus), caseData);

            // Save judgement/order PDFs into the case's "Orders" folder.
            await uploadSciOrderPdfs({
              user,
              caseId: bulkDocRef.id,
              caseNo: caseData.caseNo,
              caseCollection: caseStatus,
              judgementOrders: asList(details.judgementOrders),
            });

            try {
              await getToken(messaging, {
                vapidKey: "BOSi0E-WalUbqOuB5su9XW2J39ot0D4x-fSPRYo_AxMVloqe7jIkpYXf5J007lXs1D_ZZB9YH1Hc0X-7jQrqR38",
              });
            } catch (err) {
              console.warn("Could not get FCM token:", err.message);
            }

            await createEventAndReminder(caseData, user, bulkDocRef.id, caseStatus);
            successfulExports++;
            if (caseStatus === "pending") currentPendingCount++;

            setExportProgress(prev => ({
              ...prev, exported: prev.exported + 1,
              currentCase: `Completed: ${item.diaryNo || `Case ${index + 1}`}`
            }));
          }

          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (caseError) {
          console.error(`Error processing case at index ${index}:`, caseError);
          failedExports++;
          const item = results[index];
          setExportProgress(prev => ({
            ...prev, exported: prev.exported + 1,
            failedCases: [...prev.failedCases, item?.diaryNo || 'Unknown'],
            currentCase: `Failed: ${item?.diaryNo || 'Unknown'}`
          }));
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      if (duplicatesFound.length === selectedIndices.length) {
        setDuplicateCases(duplicatesFound);
        setShowDuplicateModal(true);
      } else if (successfulExports > 0) {
        showInfo();
      } else if (duplicatesFound.length > 0) {
        setDuplicateCases(duplicatesFound);
        setShowDuplicateModal(true);
      } else if (failedExports > 0) {
        alert(`All ${failedExports} cases failed to export.`);
      }

      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (err) {
      console.error("Bulk export error:", err);
      alert(`Error exporting cases: ${err.message}`);
    } finally {
      setExporting(false);
      setExportProgress({ total: 0, exported: 0, currentCase: null, startTime: null, failedCases: [] });
    }
  };

  // --- Render ---

  const renderResultsList = () => {
    const results = getRecords();

    if (!Array.isArray(results) || results.length === 0) {
      if (data.diaryNo || data.diary_no) {
        // The record may carry diaryNo + diaryYear as separate fields, or a
        // combined "5337/2024" value — getDiaryParts handles both.
        const { no: dNo, year: parsedYear } = getDiaryParts(data);
        const dYear = data.diaryYear || data.diary_year || data.year || parsedYear;
        if (!caseDetails) {
          fetchDetails(dNo, dYear);
        }
        return null;
      }
      return <p className="text-slate-500 text-center py-8">No results found.</p>;
    }

    const filteredRows = getFilteredRows();
    const totalPages = Math.max(1, Math.ceil(filteredRows.length / casesPerPage));
    const page = Math.min(currentPage, totalPages);
    const indexOfLast = page * casesPerPage;
    const indexOfFirst = indexOfLast - casesPerPage;
    const pageRows = filteredRows.slice(indexOfFirst, indexOfLast);

    // Window of up to 5 page numbers around the current page.
    const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
    const pageNumbers = [];
    for (let i = startPage; i <= Math.min(totalPages, startPage + 4); i++) pageNumbers.push(i);

    return (
      <>
        {/* Toolbar — search + bulk actions */}
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search in results..."
              className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                title="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-3">
            {selectedCases.size > 0 && (
              <span className="text-sm text-slate-500">{selectedCases.size} selected</span>
            )}
            <button
              onClick={exportSelectedCases}
              className={`flex h-10 items-center gap-2 rounded-lg px-5 text-sm font-semibold transition-colors ${
                exporting || selectedCases.size === 0
                  ? "cursor-not-allowed bg-slate-100 text-slate-400"
                  : "bg-yellow-500 text-slate-900 hover:bg-yellow-400"
              }`}
              disabled={exporting || selectedCases.size === 0}
            >
              {exporting ? (
                <>
                  <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Exporting ({exportProgress.exported}/{exportProgress.total})
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Export Selected ({selectedCases.size})
                </>
              )}
            </button>
          </div>
        </div>

        {filteredRows.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-base font-medium text-slate-700">No cases match your search</p>
            <button
              type="button"
              onClick={handleClearSearch}
              className="mt-2 text-sm font-medium text-yellow-600 hover:underline"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-yellow-500"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3">Diary No</th>
                  <th className="px-4 py-3">Case Number</th>
                  <th className="px-4 py-3">Petitioner</th>
                  <th className="px-4 py-3">Respondent</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="w-16 px-4 py-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(({ item, index }) => {
                  const { no: diaryNum, year: diaryYear } = getDiaryParts(item);
                  const openCase = () => fetchDetails(diaryNum, diaryYear);
                  const cellClass = "cursor-pointer px-4 py-3.5 text-sm text-slate-600";

                  return (
                    <tr
                      key={index}
                      className={`border-b border-slate-100 transition-colors duration-150 ${
                        selectedCases.has(index) ? "bg-yellow-50/60" : "hover:bg-slate-50"
                      }`}
                    >
                      <td className="px-4 py-3.5">
                        <input
                          type="checkbox"
                          checked={selectedCases.has(index)}
                          onChange={() => handleCaseSelect(index)}
                          className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-yellow-500"
                        />
                      </td>
                      <td
                        className="cursor-pointer whitespace-nowrap px-4 py-3.5 text-sm font-medium text-slate-900"
                        onClick={openCase}
                      >
                        {item.diaryText || (diaryYear ? `${diaryNum}/${diaryYear}` : item.diaryNo) || ""}
                      </td>
                      <td className={`${cellClass} max-w-[250px]`} onClick={openCase}>
                        {item.caseNo || "—"}
                      </td>
                      <td className={`${cellClass} max-w-[200px]`} onClick={openCase}>
                        {item.petitioner || "—"}
                      </td>
                      <td className={`${cellClass} max-w-[200px]`} onClick={openCase}>
                        {item.respondent || "—"}
                      </td>
                      <td className="cursor-pointer px-4 py-3.5" onClick={openCase}>
                        {item.status ? (
                          <span className={`${STATUS_PILL_BASE} ${statusPillSoft(item.status)}`}>
                            {item.status}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <button
                          type="button"
                          onClick={openCase}
                          title="View case details"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer — result range + pagination */}
        {filteredRows.length > 0 && (
          <div className="flex flex-col items-center gap-4 border-t border-slate-100 px-4 py-4 sm:flex-row sm:justify-between">
            <p className="text-sm text-slate-500">
              Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredRows.length)} of {filteredRows.length} results
            </p>

            {totalPages > 1 && (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  title="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {startPage > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                    >
                      1
                    </button>
                    {startPage > 2 && <span className="px-1 text-slate-400">...</span>}
                  </>
                )}

                {pageNumbers.map((number) => (
                  <button
                    key={number}
                    onClick={() => setCurrentPage(number)}
                    className={`h-9 min-w-[2.25rem] rounded-lg px-2 text-sm font-medium transition-colors ${
                      page === number
                        ? "bg-yellow-500 text-slate-900"
                        : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {number}
                  </button>
                ))}

                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                  <>
                    {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                      <span className="px-1 text-slate-400">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  title="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  // Renders a labelled list of names (parties/advocates).
  const PartyList = ({ label, items }) => (
    <div className="flex py-2">
      <span className="w-48 text-sm text-slate-600 shrink-0">{label}:</span>
      <div className="flex-1">
        {items.length > 0 ? (
          items.map((p, i) => <div key={i} className="font-bold">{p}</div>)
        ) : (
          <span className="font-bold">N/A</span>
        )}
      </div>
    </div>
  );

  // Generic table section for the rich tab data (hearings, notices, etc.).
  // `columns` is an array of { key, label, render? }.
  const TableSection = ({ title, rows, columns }) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-bold mb-2">{title} ({rows.length})</h3>
        <hr className="border-slate-200 mb-4" />
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                {columns.map((c) => (
                  <th key={c.key} className="py-2 px-3 text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-slate-100 align-top">
                  {columns.map((c) => (
                    <td key={c.key} className="py-2 px-3">
                      {c.render ? c.render(row) : (row[c.key] ?? "") || "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCaseDetailsContent = () => {
    if (!caseDetails) return null;

    const core = getCore(caseDetails);
    const petitioners = asList(core.petitioners ?? core.petitioner_s);
    const respondents = asList(core.respondents ?? core.respondent_s);
    const petAdvocates = asList(core.petitionerAdvocates ?? core.petitioner_advocate_s);
    const resAdvocates = asList(core.respondentAdvocates ?? core.respondent_advocate_s);
    const caseNumberStr = getCaseNumberStr(core);
    const diaryNumber = getDiaryNumber(core);
    const status = core.disposalStatus || caseDetails?.searchResult?.status || core.status || "";
    const filedOn = [core.filedDate, core.filedTime].filter(Boolean).join(" ") || core.filed_on || "";
    const judges = asList(core.judges);
    const bench = asList(core.bench);

    const firstPetitioner = petitioners.length > 0 ? petitioners[0].replace(/^\d+\s*/, "") : "Unknown";
    const firstRespondent = respondents.length > 0 ? respondents[0].replace(/^\d+\s*/, "") : "Unknown";

    // Rich tab sections (present only in the new normalized DTO).
    const listingDates = asList(caseDetails.listingDates);
    const interlocutoryApplications = asList(caseDetails.interlocutoryApplications);
    const notices = asList(caseDetails.notices);
    const earlierCourtDetails = asList(caseDetails.earlierCourtDetails);
    const judgementOrders = asList(caseDetails.judgementOrders);
    const otherDocuments = asList(caseDetails.otherDocuments);

    return (
      <div className="space-y-4">
        {/* Header Card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">{firstPetitioner} vs. {firstRespondent}</h2>
            <span className={`px-3 py-1 rounded-full text-sm text-white ${statusPillSolid(status)}`}>
              {status || "Unknown Status"}
            </span>
          </div>
          <p className="text-slate-500 text-sm mt-1">Diary No. {diaryNumber}</p>
        </div>

        {/* Case Information */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-lg font-bold mb-2">Case Information</h3>
          <hr className="border-slate-200 mb-4" />
          <InfoRow label="Diary Number" value={diaryNumber} />
          <InfoRow label="Filed On" value={filedOn} />
          {core.registeredDate && <InfoRow label="Registered On" value={core.registeredDate} />}
          {core.verifiedDate && <InfoRow label="Verified On" value={core.verifiedDate} />}
          {core.section && <InfoRow label="Section" value={core.section} />}
          <InfoRow label="Case Number" value={caseNumberStr} />
          {core.cnrNumber && <InfoRow label="CNR Number" value={core.cnrNumber} />}
          <InfoRow label="Present/Last Listed On" value={core.presentLastListedOn || core.present_last_listed_on} />
          {bench.length > 0 && <InfoRow label="Bench" value={bench.join(", ")} />}
          <InfoRow label="Status/Stage" value={core.statusStage || core.status_stage} />
          {core.disposalDate && <InfoRow label="Disposal Date" value={[core.disposalDate, core.disposalMonth, core.disposalYear].filter(Boolean).join("-")} />}
          {core.disposalType && <InfoRow label="Disposal Type" value={core.disposalType} />}
          {judges.length > 0 && <InfoRow label="Judges" value={judges.join(", ")} />}
          {core.category && <InfoRow label="Category" value={core.category} />}
        </div>

        {/* Client Information */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-lg font-bold mb-2">Client Information</h3>
          <hr className="border-slate-200 mb-4" />
          <PartyList label="Petitioner(s)" items={petitioners} />
          <PartyList label="Respondent(s)" items={respondents} />
        </div>

        {/* Advocate Information */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-lg font-bold mb-2">Advocate Information</h3>
          <hr className="border-slate-200 mb-4" />
          <PartyList label="Petitioner Advocate(s)" items={petAdvocates} />
          <PartyList label="Respondent Advocate(s)" items={resAdvocates} />
        </div>

        {/* Hearing / Listing History */}
        <TableSection
          title="Listing / Hearing History"
          rows={listingDates}
          columns={[
            { key: "clDate", label: "Date" },
            { key: "stage", label: "Stage" },
            { key: "purpose", label: "Purpose" },
            { key: "judges", label: "Judges", render: (r) => asList(r.judges).join(", ") || "—" },
            { key: "remarks", label: "Remarks" },
            { key: "listedStatus", label: "Listed" },
          ]}
        />

        {/* Interlocutory Applications */}
        <TableSection
          title="Interlocutory Applications"
          rows={interlocutoryApplications}
          columns={[
            { key: "registrationNumber", label: "Reg. No." },
            { key: "particular", label: "Particular" },
            { key: "filedBy", label: "Filed By" },
            { key: "filingDate", label: "Filing Date" },
            { key: "status", label: "Status" },
          ]}
        />

        {/* Notices */}
        <TableSection
          title="Notices"
          rows={notices}
          columns={[
            { key: "noticeType", label: "Type" },
            { key: "name", label: "Name" },
            { key: "station", label: "Station" },
            { key: "issueDate", label: "Issued" },
            { key: "returnableDate", label: "Returnable" },
            { key: "dispatchDate", label: "Dispatched" },
          ]}
        />

        {/* Earlier Court Details */}
        <TableSection
          title="Earlier Court Details"
          rows={earlierCourtDetails}
          columns={[
            { key: "court", label: "Court" },
            { key: "agencyState", label: "State" },
            { key: "caseNumber", label: "Case No." },
            { key: "orderDate", label: "Order Date" },
            { key: "judges", label: "Judges", render: (r) => asList(r.judges).join(", ") || "—" },
          ]}
        />

        {/* Judgements / Orders */}
        <TableSection
          title="Judgements / Orders"
          rows={judgementOrders}
          columns={[
            { key: "documentName", label: "Document", render: (r) => cleanOrderName(r.documentName || r.description) },
            { key: "date", label: "Date" },
            {
              key: "url",
              label: "Link",
              render: (r) =>
                r.url || r.documentUrl ? (
                  <a
                    href={r.url || r.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                  >
                    View
                  </a>
                ) : (
                  "—"
                ),
            },
          ]}
        />

        {/* Other Documents */}
        <TableSection
          title="Other Documents"
          rows={otherDocuments}
          columns={[
            { key: "documentNumber", label: "Doc No." },
            { key: "documentType", label: "Type" },
            { key: "filedBy", label: "Filed By" },
            { key: "filingDate", label: "Filing Date" },
          ]}
        />

        {/* Export Button */}
        <button
          onClick={handleExportCase}
          disabled={exporting}
          className={`bg-yellow-500 text-gray-900 py-2.5 px-8 rounded-lg shadow-sm flex items-center justify-center w-full font-bold hover:bg-gray-200 ${exporting ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <span className="mr-2">☁</span>
          {exporting ? "Importing..." : "Import Case"}
        </button>
      </div>
    );
  };

  return (
    <>
                        <div className="min-h-screen bg-[#F8FAFC]">
        <main className="px-4 sm:px-6 lg:px-8 py-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-sm">
            <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">Find Cases</Link>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <Link to="/portal/fetchfromsupremecourt" className="text-slate-500 hover:text-slate-800">Supreme Court</Link>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <span className="font-medium text-slate-800">{selectedCase ? "Import Case" : "Results"}</span>
          </div>
          <div className="mt-4"><FindCaseStepper current={selectedCase ? 4 : 3} /></div>

          {selectedCase && (
            <div className="mt-4">
              <button
                onClick={() => { setSelectedCase(null); setCaseDetails(null); }}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="h-4 w-4" /> Back to results
              </button>
            </div>
          )}

          {/* Summary stat cards — list view only */}
          {!selectedCase && !loading && getRecords().length > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
              {(() => {
                const records = getRecords();
                const pending = records.filter((r) => (r.status || "").toLowerCase() === "pending").length;
                return [
                  { label: "Total Found", value: records.length, Icon: Users, tint: "bg-indigo-50 text-indigo-500" },
                  { label: "Selected", value: selectedCases.size, Icon: CheckSquare, tint: "bg-blue-50 text-blue-500" },
                  { label: "Pending", value: pending, Icon: Clock, tint: "bg-green-50 text-green-500" },
                  { label: "Disposed", value: records.length - pending, Icon: CircleCheck, tint: "bg-red-50 text-red-500" },
                ];
              })().map(({ label, value, Icon, tint }) => (
                <div
                  key={label}
                  className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold leading-tight text-slate-900">{value}</p>
                    <p className="truncate text-xs text-slate-500">{label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 mb-8">
            <div
              className={`text-slate-800 ${
                selectedCase
                  ? "w-full"
                  : "w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              }`}
            >
              {error && (
                <p className="mx-4 mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 ring-1 ring-red-100">{error}</p>
              )}

              {loading && (
                <div className="flex justify-center py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-yellow-500"></div>
                </div>
              )}

              {!selectedCase && !loading && renderResultsList()}

              {selectedCase && caseDetails && !loading && renderCaseDetailsContent()}
            </div>
          </div>
        </main>
      </div>

      {/* Export Progress Modal */}
      {exporting && exportProgress.total > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Exporting Your Cases</h3>
              <p className="text-sm text-gray-600 mb-6">Please wait while we process and export your selected cases...</p>

              {/* Progress Bar */}
              <div className="relative mb-6">
                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out relative"
                    style={{
                      width: exportProgress.total > 0
                        ? `${(exportProgress.exported / exportProgress.total) * 100}%`
                        : '0%'
                    }}
                  >
                    <div className="absolute inset-0 bg-white bg-opacity-20 animate-pulse"></div>
                  </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-bold text-gray-700">
                    {exportProgress.total > 0
                      ? `${Math.round((exportProgress.exported / exportProgress.total) * 100)}%`
                      : '0%'}
                  </span>
                </div>
              </div>

              {/* Stats */}
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-600">Cases Exported:</span>
                  <span className="text-lg font-bold text-blue-600">
                    {exportProgress.exported} / {exportProgress.total}
                  </span>
                </div>
                {exportProgress.exported > 0 && exportProgress.startTime && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Estimated Time:</span>
                    <span className="text-sm font-semibold text-gray-700">
                      {(() => {
                        const elapsed = Date.now() - exportProgress.startTime;
                        const avgTimePerCase = elapsed / exportProgress.exported;
                        const remaining = exportProgress.total - exportProgress.exported;
                        const estimatedSeconds = Math.ceil((remaining * avgTimePerCase) / 1000);
                        if (estimatedSeconds < 60) return `${estimatedSeconds}s remaining`;
                        return `${Math.ceil(estimatedSeconds / 60)}m remaining`;
                      })()}
                    </span>
                  </div>
                )}
                {exportProgress.failedCases.length > 0 && (
                  <div className="mt-2 text-xs text-red-600">
                    Failed: {exportProgress.failedCases.length} case(s)
                  </div>
                )}
              </div>

              {exportProgress.currentCase && (
                <div className="flex items-center justify-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <p className="text-xs text-gray-500 truncate max-w-[250px]">{exportProgress.currentCase}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <InfoModal
        isOpen={isInfoModalOpen}
        onClose={closeInfoModal}
        title="Information"
        message="Case exported successfully! For exported case the default reminder is set to 1 day before the next hearing date."
      />

      {/* Duplicate Modal (single export) */}
      <InfoModal
        isOpen={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        title="Duplicate Case Found"
        message={`Case ${duplicateCaseInfo?.caseNo} is already exported and saved in your profile under ${duplicateCaseInfo?.collection} cases.`}
      />

      {/* Duplicate Modal (bulk export) */}
      <InfoModal
        isOpen={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        title="Duplicate Cases Found"
        message={`${duplicateCases.length} case(s) are already exported and saved in your profile.`}
      />

      {/* Case Limit Dialog */}
      <CaseLimitDialog
        isOpen={showCaseLimitDialog}
        onClose={() => setShowCaseLimitDialog(false)}
        currentCount={caseLimitData.currentCount}
        maxLimit={caseLimitData.maxLimit}
        planName={caseLimitData.planName}
      />
    </>
  );
}

export default SupremeCourtResults;
